import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import {
  NpsSummaryItem,
  NpsDoctorRankingItem,
  NpsHealthUnitRankingItem,
  NpsWordCloudItem,
  NpsCommentItem,
  NpsNegativeDriversAnalytics,
  NpsAnalyticsFilters,
  NpsFilterOption,
  ListNpsDoctorRankingFullResponse,
  ListNpsHealthUnitRankingFullResponse,
  NpsDoctorReportResponse
} from '@/Network/Types/Responses/NpsResponses'
import { NpsRankingFullFilters, NpsDoctorReportFilters } from '@/Network/Types/Requests/NpsRequests'

function buildQueryString<T extends object>(filters?: T): string {
  if (!filters) return ''
  const qs = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value == null) return
    if (Array.isArray(value)) {
      value.forEach((v) => qs.append(key, String(v)))
    } else {
      qs.set(key, String(value))
    }
  })
  const result = qs.toString()
  return result ? `?${result}` : ''
}

export default class NpsAnalyticsService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/nps/analytics'
  }

  async getSummary(filters?: NpsAnalyticsFilters): Promise<NpsSummaryItem[]> {
    const { data } = await request.get<NpsSummaryItem[]>(
      `${this.getCompletePath()}/summary${buildQueryString(filters)}`
    )
    return data
  }

  async getDoctorRanking(filters?: NpsAnalyticsFilters): Promise<NpsDoctorRankingItem[]> {
    const { data } = await request.get<NpsDoctorRankingItem[]>(
      `${this.getCompletePath()}/doctor-ranking${buildQueryString(filters)}`
    )
    return data
  }

  async getHealthUnitRanking(filters?: NpsAnalyticsFilters): Promise<NpsHealthUnitRankingItem[]> {
    const { data } = await request.get<NpsHealthUnitRankingItem[]>(
      `${this.getCompletePath()}/health-unit-ranking${buildQueryString(filters)}`
    )
    return data
  }

  async getWordCloud(filters?: NpsAnalyticsFilters): Promise<NpsWordCloudItem[]> {
    const { data } = await request.get<NpsWordCloudItem[]>(
      `${this.getCompletePath()}/word-cloud${buildQueryString(filters)}`
    )
    return data
  }

  async getComments(filters?: NpsAnalyticsFilters): Promise<NpsCommentItem[]> {
    const { data } = await request.get<NpsCommentItem[]>(
      `${this.getCompletePath()}/comments${buildQueryString(filters)}`
    )
    return data
  }

  /**
   * Filtros do dashboard escopados ao NPS (Unidade / Escala / Médico).
   *
   * Substituem os antigos endpoints admin de outros módulos (health-units/schedules), que
   * exigiam papéis admin fora do NPS e deixavam os dropdowns vazios para quem só tinha permissão
   * de NPS (Causa B da issue #1827). Carregamento não-crítico → fallback silencioso para [].
   */
  async listFilterHealthUnits(): Promise<NpsFilterOption[]> {
    try {
      const { data } = await request.get<NpsFilterOption[]>(
        `${this.getCompletePath()}/filters/health-units`
      )
      return data
    } catch {
      return []
    }
  }

  async listFilterSchedules(): Promise<NpsFilterOption[]> {
    try {
      const { data } = await request.get<NpsFilterOption[]>(
        `${this.getCompletePath()}/filters/schedules`
      )
      return data
    } catch {
      return []
    }
  }

  async listFilterDoctors(): Promise<NpsFilterOption[]> {
    try {
      const { data } = await request.get<NpsFilterOption[]>(
        `${this.getCompletePath()}/filters/doctors`
      )
      return data
    } catch {
      return []
    }
  }

  async getNegativeDrivers(filters?: NpsAnalyticsFilters): Promise<NpsNegativeDriversAnalytics> {
    const { data } = await request.get<NpsNegativeDriversAnalytics>(
      `${this.getCompletePath()}/negative-drivers${buildQueryString(filters)}`
    )
    return data
  }

  async exportReport(filters?: NpsAnalyticsFilters): Promise<HTMLAnchorElement> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'text/csv; charset=utf-8'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'relatorio-nps.csv')
      return link
    } catch (e) {
      console.error('Erro ao exportar relatório NPS', e)
      throw new Error('Ocorreu um erro ao exportar o relatório.')
    }
  }

  async getDoctorRankingFull(
    filters?: NpsRankingFullFilters
  ): Promise<ListNpsDoctorRankingFullResponse> {
    try {
      const { data } = await request.get<ListNpsDoctorRankingFullResponse>(
        `${this.getCompletePath()}/doctor-ranking/full${buildQueryString(filters)}`
      )
      return data
    } catch {
      return { data: [], count: 0 }
    }
  }

  async getHealthUnitRankingFull(
    filters?: NpsRankingFullFilters
  ): Promise<ListNpsHealthUnitRankingFullResponse> {
    try {
      const { data } = await request.get<ListNpsHealthUnitRankingFullResponse>(
        `${this.getCompletePath()}/health-unit-ranking/full${buildQueryString(filters)}`
      )
      return data
    } catch {
      return { data: [], count: 0 }
    }
  }

  async getDoctorReport(
    doctorId: number,
    filters?: NpsDoctorReportFilters
  ): Promise<NpsDoctorReportResponse | null> {
    try {
      const { data } = await request.get<NpsDoctorReportResponse>(
        `${this.getCompletePath()}/doctor-report/${doctorId}${buildQueryString(filters)}`
      )
      return data
    } catch {
      return null
    }
  }

  async exportDoctorRanking(filters?: NpsRankingFullFilters): Promise<void> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/doctor-ranking/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'ranking-medicos.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      throw new Error('Ocorreu um erro ao exportar o ranking de médicos.')
    }
  }

  async exportHealthUnitRanking(filters?: NpsRankingFullFilters): Promise<void> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/health-unit-ranking/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'ranking-unidades.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      throw new Error('Ocorreu um erro ao exportar o ranking de unidades.')
    }
  }

  async exportDoctorReport(doctorId: number, filters?: NpsDoctorReportFilters): Promise<void> {
    try {
      const { data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/doctor-report/${doctorId}/export${buildQueryString(filters)}`,
        undefined,
        undefined,
        'blob'
      )
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `relatorio-medico-${doctorId}.xlsx`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      throw new Error('Ocorreu um erro ao exportar o relatório do médico.')
    }
  }
}
